import { motion } from "framer-motion";
import { Calendar, CheckCircle, ExternalLink } from "lucide-react";

const CALENDAR_BOOKING_URL = "https://calendar.app.google/GKLhVhu2DP1zxbcFA";

export const BookingPage = () => {
  return (
    <div>
      <section className="py-20 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl text-[#2D4A6D] mb-6 font-serif">Book a Strategy Call</h1>
          <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            A focused 30-minute conversation to identify operational requirements, clarify priorities, and determine the appropriate structure.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#2D4A6D] rounded-full mb-6">
              <Calendar className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl text-[#2D4A6D] font-serif mb-4">Choose a Time That Works</h2>
            <p className="text-slate-600 mb-8">
              Select an available time directly from the calendar. An instant confirmation and calendar invite will follow.
            </p>
            
            <a 
              href={CALENDAR_BOOKING_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="booking-calendar-link"
              className="inline-flex items-center gap-3 bg-[#2D4A6D] hover:bg-[#1F3A5F] text-white px-10 py-5 text-lg font-medium transition-colors"
            >
              <Calendar className="w-6 h-6" />
              Open Calendar
              <ExternalLink className="w-5 h-5" />
            </a>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            {[
              { title: "Instant Confirmation", desc: "Immediate confirmation with calendar invite" },
              { title: "See Real Availability", desc: "Choose from actual open time slots" },
              { title: "Video Link Included", desc: "Google Meet link automatically added" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-200">
                <CheckCircle className="w-6 h-6 text-[#2D4A6D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-[#2D4A6D] mb-1">{item.title}</h4>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl text-[#2D4A6D] mb-4 font-serif">What to Expect</h2>
            <div className="w-16 h-0.5 bg-[#2D4A6D] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: "01", title: "Discovery", desc: "Discuss operational challenges, priorities, systems, and workflow gaps." },
              { num: "02", title: "Recommendations", desc: "Identify potential areas of support, systems, workflows, and engagement structure." },
              { num: "03", title: "Next Steps", desc: "Align on scope, expectations, timeline, and requirements." }
            ].map((step, i) => (
              <div key={i} className="p-6 bg-white border border-slate-200 text-center">
                <div className="text-3xl font-light text-[#2D4A6D] mb-3">{step.num}</div>
                <h4 className="text-base font-semibold text-[#2D4A6D] mb-2">{step.title}</h4>
                <p className="text-sm text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-600 mt-8 italic">This is not a sales call. It's a clarity call.</p>
        </div>
      </section>
    </div>
  );
};
